import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listErrors, type ErrorListQuery } from "@/api/lookups";
import type { ErrorListItem } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/table/DataTable";

interface Filters {
  phase: string;
  provider: string;
  since: string;
  until: string;
}

const EMPTY: Filters = { phase: "", provider: "", since: "", until: "" };

function toQuery(f: Filters): ErrorListQuery {
  const q: ErrorListQuery = { limit: 100 };
  if (f.phase) q.phase = Number.parseInt(f.phase, 10);
  if (f.provider) q.provider = f.provider;
  if (f.since) q.since = f.since;
  if (f.until) q.until = f.until;
  return q;
}

export function ErrorsRegistry() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [draft, setDraft] = useState<Filters>(EMPTY);

  const errors = useQuery({
    queryKey: ["errors", "list", filters],
    queryFn: () => listErrors(toQuery(filters)),
    refetchInterval: 30_000,
  });

  const columns: ColumnDef<ErrorListItem, unknown>[] = useMemo(
    () => [
      {
        // «№ ошибки» — global sequential counter from the backend (FR-31
        // follow-up). Replaces the UUID prefix as the operator-visible
        // identity of an error event. Rendered as «№N» so it reads as a
        // counter, not a meeting reference; keeps it visually distinct
        // from the «№ встречи» column right next to it.
        id: "error_no",
        header: t("errorsRegistry.columns.errorNo"),
        cell: (info) => `№${info.row.original.error_no}`,
      },
      {
        id: "created_at",
        header: t("errorsRegistry.columns.createdAt"),
        cell: (info) =>
          new Date(info.row.original.created_at).toLocaleString("ru"),
      },
      {
        // «№ встречи» — JOINed from meetings.meeting_no on the server. The
        // link still uses the UUID `meeting_id` for the route param (the
        // meeting card route is keyed by UUID, not by sequential number),
        // but the visible label is «№N» so operators can cross-reference
        // the meetings registry without copying UUID fragments.
        id: "meeting",
        header: t("errorsRegistry.columns.meeting"),
        cell: (info) => (
          <Link
            to={"/meetings/$meetingId"}
            params={{ meetingId: info.row.original.meeting_id }}
            className="text-primary underline-offset-4 hover:underline"
          >
            №{info.row.original.meeting_no}
          </Link>
        ),
      },
      {
        id: "phase",
        header: t("errorsRegistry.columns.phase"),
        cell: (info) => info.row.original.phase,
      },
      {
        id: "step",
        header: t("errorsRegistry.columns.step"),
        cell: (info) => info.row.original.step,
      },
      {
        id: "provider",
        header: t("errorsRegistry.columns.provider"),
        cell: (info) => info.row.original.provider ?? t("common.dash"),
      },
      {
        id: "message",
        header: t("errorsRegistry.columns.message"),
        cell: (info) => info.row.original.message ?? t("common.dash"),
      },
      {
        id: "attempt",
        header: t("errorsRegistry.columns.attempt"),
        cell: (info) => info.row.original.attempt,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">{t("errorsRegistry.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("errorsRegistry.subtitle")}
        </p>
      </header>
      <form
        className="grid grid-cols-1 gap-2 md:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
        }}
        data-testid="errors-filters"
      >
        <select
          aria-label={t("errorsRegistry.filters.phase")}
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={draft.phase}
          onChange={(e) => setDraft((d) => ({ ...d, phase: e.target.value }))}
        >
          <option value="">{t("errorsRegistry.filters.anyPhase")}</option>
          {[1, 2, 3, 4, 5].map((p) => (
            <option key={p} value={String(p)}>
              {p}
            </option>
          ))}
        </select>
        <Input
          aria-label={t("errorsRegistry.filters.provider")}
          placeholder={t("errorsRegistry.filters.provider")}
          value={draft.provider}
          onChange={(e) =>
            setDraft((d) => ({ ...d, provider: e.target.value }))
          }
        />
        <Input
          aria-label={t("errorsRegistry.filters.since")}
          type="date"
          value={draft.since}
          onChange={(e) => setDraft((d) => ({ ...d, since: e.target.value }))}
        />
        <Input
          aria-label={t("errorsRegistry.filters.until")}
          type="date"
          value={draft.until}
          onChange={(e) => setDraft((d) => ({ ...d, until: e.target.value }))}
        />
        <Button type="submit" size="sm" variant="outline">
          {t("common.filterApply")}
        </Button>
      </form>
      <DataTable
        data={errors.data ?? []}
        columns={columns}
        testId="errors-table"
        isLoading={errors.isLoading}
      />
    </div>
  );
}
